import type { PublicId } from 'flow/store/engine/types';
import { generateOperations } from '../../changelog';
import { executePredicatedProcessors } from '../../lib/predicatedProcessor';
import createOperationProcessors, { createChangesByOperationBuffer } from '../../operationProcessors';
import type { ChangesByOperation, RFC6902Operation, ChangelogInputBundle } from '../../types';
import mergeDiffFormatter from './mergeDiffFormatter';
import type { MergeDiffResult } from './types';
import { isActionRuleGroupingModifiedChange } from '../../guards';

/**
 * Processes a list of operations and creates Changes representing them.
 * @param operations operations that happened from versionA to versionB
 * @param versions a bundle of versions to compare
 * @returns a record of changes keyed by operation type.
 */
function computeChangesPerOperation(args: {
  A: ChangelogInputBundle;
  B: ChangelogInputBundle;
  operations: RFC6902Operation[];
}): ChangesByOperation {
  const { operations, A, B } = args;
  const changesBuffer = createChangesByOperationBuffer();
  const operationProcessors = createOperationProcessors({ changesBuffer, A, B });
  executePredicatedProcessors({ items: operations, processorRegistry: operationProcessors });
  return changesBuffer.package();
}

export default function generateMergeDiff(args: {
  A: ChangelogInputBundle;
  B: ChangelogInputBundle;
}): MergeDiffResult {
  const { A, B } = args;
  const operations = generateOperations({ A, B });

  const {
    add: addedChanges,
    modify: modifiedChanges,
  } = computeChangesPerOperation({ operations, A, B });

  // special case:
  // we move the modified action rule to be handled by the added formatter
  // since we will only be adding rules at this time
  const ruleModifiedOps = modifiedChanges.filter(change => (
    isActionRuleGroupingModifiedChange(change)
  ));

  const adjustedAddedChanges = [...addedChanges, ...ruleModifiedOps];

  const filteredModifiedChanges = modifiedChanges.filter(change => (
    !isActionRuleGroupingModifiedChange(change)
  ));

  return {
    added: new Set<PublicId>(
      mergeDiffFormatter({ A, B, changes: adjustedAddedChanges }),
    ),
    modified: new Set<PublicId>(
      mergeDiffFormatter({ A, B, changes: filteredModifiedChanges }),
    ),
  };
}
